import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { ReplyTemplateMeta } from '@/lib/api/types'
import { type ReplyTemplateInput, useSaveReplyTemplate } from './reply-templates'

/**
 * Write or change a reply template: a title, the tickets it's suggested for, and the text with
 * placeholders that are filled in from the ticket when it's used.
 */
export function ReplyTemplateEditor({
  open,
  onOpenChange,
  meta,
  initial,
  id,
  heading,
  note,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  meta: ReplyTemplateMeta
  initial: ReplyTemplateInput
  /** Set when changing an existing template. */
  id?: string
  heading: string
  /** Shown under the heading, e.g. how a reply was turned into a template. */
  note?: string
  onSaved?: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={heading}
        description={
          note ??
          'Placeholders in braces are filled in from the ticket when someone uses the template, and the reply can still be edited before it’s sent.'
        }
        className="max-w-2xl"
      >
        {/* Mounted only while open, so it starts from `initial` every time. */}
        {open && (
          <EditorForm
            meta={meta}
            initial={initial}
            id={id}
            onDone={() => {
              onOpenChange(false)
              onSaved?.()
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function EditorForm({
  meta,
  initial,
  id,
  onDone,
}: {
  meta: ReplyTemplateMeta
  initial: ReplyTemplateInput
  id?: string
  onDone: () => void
}) {
  const save = useSaveReplyTemplate()
  const [values, setValues] = useState(initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  // The last cursor position in the text; placeholders go at the end until it's been clicked into.
  const caret = useRef<{ start: number; end: number } | null>(null)

  const insert = (key: string) => {
    const el = bodyRef.current
    const token = `{${key}}`
    const { start, end } = caret.current ?? { start: values.body.length, end: values.body.length }
    setValues((v) => ({ ...v, body: v.body.slice(0, start) + token + v.body.slice(end) }))
    caret.current = { start: start + token.length, end: start + token.length }
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        setErrors({})
        save.mutate(
          { id, title: values.title.trim(), body: values.body.trim(), category: values.category },
          {
            onSuccess: () => {
              toast.success(id ? 'Template saved.' : 'Template added. Everyone on the team can use it now.')
              onDone()
            },
            onError: (err) => {
              if (isApiError(err) && err.isValidation) {
                setErrors(Object.fromEntries(Object.entries(err.errors).map(([k, v]) => [k, v[0] ?? ''])))
              } else {
                toast.error(errorMessage(err))
              }
            },
          },
        )
      }}
    >
      <Field label="Title" htmlFor="template-title" required error={errors.title}>
        <Input
          id="template-title"
          value={values.title}
          onChange={(e) => setValues((v) => ({ ...v, title: e.target.value }))}
          maxLength={120}
          placeholder="For example: Rankings differ from Google"
        />
      </Field>
      <Field
        label="Suggest it for"
        htmlFor="template-category"
        error={errors.category}
        hint="It comes first on tickets of this kind. Every template can be used on any ticket."
      >
        <NativeSelect
          id="template-category"
          value={values.category ?? ''}
          onChange={(e) => setValues((v) => ({ ...v, category: e.target.value || null }))}
        >
          <option value="">Any ticket</option>
          {meta.categories.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field label="Reply" htmlFor="template-body" required error={errors.body}>
        <Textarea
          id="template-body"
          ref={bodyRef}
          rows={11}
          value={values.body}
          onChange={(e) => setValues((v) => ({ ...v, body: e.target.value }))}
          onSelect={(e) =>
            (caret.current = { start: e.currentTarget.selectionStart, end: e.currentTarget.selectionEnd })
          }
          maxLength={5000}
        />
      </Field>
      <div className="grid gap-2">
        <p className="text-xs font-medium text-ink-muted">Insert a placeholder</p>
        <div className="flex flex-wrap gap-1.5">
          {meta.placeholders.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => insert(p.key)}
              title={`${p.label}, e.g. ${p.example}`}
              className="rounded-full border border-border-strong bg-surface px-2.5 py-1 font-mono text-[11px] text-ink-muted hover:border-ink/40 hover:text-ink"
            >
              {`{${p.key}}`}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button
          type="submit"
          loading={save.isPending}
          disabled={values.title.trim().length < 2 || values.body.trim().length < 2}
        >
          {id ? 'Save template' : 'Add template'}
        </Button>
      </div>
    </form>
  )
}
